import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiPut, apiDelete, formatMoney, t } from "../api_helper"
import { icon } from "../lib/icons"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, showOfflinePlaceholder, alertOffline } from "../lib/offline_ui"
import "../lib/paywall"

export default class extends Controller {
  static targets = [
    "list", "summary", "form", "addBtn", "dirIOweBtn", "dirOwedBtn",
    "personName", "amount", "currency", "note", "dueDate",
    "loanToggle", "loanFields", "loanSlider", "loanKnob",
    "interestRate", "termMonths", "knownPayment", "loanToggleWrap",
    "loanDetail"
  ]

  connect() {
    this.direction = "i_owe"
    this.accounts = []
    this.payingDebtId = null
    this.isLoan = false
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/debts")
      this.debts = data.debts || []
      this.accounts = data.accounts || []
      this.summaryData = data.summary || {}
      this.renderSummary()
      this.renderList()
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasListTarget ? this.listTarget : null, () => this.load()); return }
      if (e.status === 403) window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "debts" } }))
      else console.error("Debts load error:", e)
    }
  }

  renderSummary() {
    if (!this.hasSummaryTarget || !this.summaryData) return
    const s = this.summaryData
    if (!this.debts.length) {
      this.summaryTarget.classList.add("hidden")
      return
    }
    this.summaryTarget.classList.remove("hidden")
    this.summaryTarget.innerHTML = `
      <div class="p-4 rounded-2xl" style="background-color: rgb(248,250,252);">
        <div class="flex justify-between text-sm mb-1">
          <span style="color: rgb(239,68,68);">${t("debts.i_owe")}: ${formatMoney(s.i_owe, s.currency)}</span>
          <span style="color: rgb(16,185,129);">${t("debts.owed_to_me")}: ${formatMoney(s.owed_to_me, s.currency)}</span>
        </div>
        <div class="text-xs text-center" style="color: rgb(148,163,184);">
          ${t("debts.net")}: <span style="color: ${s.net >= 0 ? "rgb(16,185,129)" : "rgb(239,68,68)"};">${formatMoney(s.net, s.currency)}</span>
        </div>
      </div>`
  }

  renderList() {
    if (!this.hasListTarget) return
    if (!this.debts.length) {
      this.listTarget.innerHTML = `
        <div class="text-center py-8 px-4 rounded-2xl" style="background-color: rgb(248,250,252);">
          <span class="block mb-2">${icon("file-text", "w-10 h-10 text-slate-300")}</span>
          <div class="text-sm" style="color: rgb(148,163,184);">${t("debts.no_debts")}</div>
        </div>`
      return
    }

    const html = this.debts.map(d => {
      const isIOwe = d.direction === "i_owe"
      const dirColor = isIOwe ? "rgb(239,68,68)" : "rgb(16,185,129)"
      const dirLabel = isIOwe ? t("debts.i_owe") : t("debts.owed_to_me")
      const settledClass = d.settled ? "opacity-50" : ""
      const isPaying = this.payingDebtId === d.id

      const paidPct = d.amount > 0 ? Math.min((d.paid_amount / d.amount) * 100, 100).toFixed(0) : 0
      const hasPaid = d.paid_amount > 0 && !d.settled

      return `
        <div class="p-3 rounded-2xl mb-2 ${settledClass}" style="background-color: rgb(248,250,252);">
          <div class="flex items-center justify-between mb-1">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-sm">${d.person_name}</span>
              <span class="text-xs px-2 py-0.5 rounded-full" style="background-color: color-mix(in srgb, ${dirColor} 15%, transparent); color: ${dirColor};">${dirLabel}</span>
              ${d.loan ? `<span class="text-xs px-2 py-0.5 rounded-full" style="background-color: rgba(99,102,241,0.15); color: rgb(99,102,241);">${t("debts.loan_badge")}</span>` : ""}
            </div>
            ${!d.settled ? `<button data-action="click->pwa-debts#togglePay" data-id="${d.id}"
                    class="text-xs px-2 py-1 rounded-lg font-semibold active:scale-[0.98] transition-transform"
                    style="color: rgb(16,185,129);">${t("debts.settle")}</button>` : ""}
          </div>

          <div class="flex items-center justify-between">
            <span class="font-semibold" style="color: ${dirColor};">${formatMoney(d.remaining, d.currency)}</span>
            ${hasPaid ? `<span class="text-xs" style="color: rgb(148,163,184);">${t("debts.paid")}: ${formatMoney(d.paid_amount, d.currency)}</span>` : ""}
          </div>

          ${hasPaid ? `
            <div style="height: 3px; border-radius: 2px; background-color: rgb(241,245,249); overflow: hidden; margin-top: 6px;">
              <div style="height: 100%; width: ${paidPct}%; border-radius: 2px; background-color: rgb(16,185,129);"></div>
            </div>` : ""}

          ${d.loan && !d.settled ? this.loanSummaryCard(d) : ""}

          ${d.note ? `<div class="text-xs mt-1" style="color: rgb(148,163,184);">${d.note}</div>` : ""}
          ${d.due_date ? `<div class="text-xs mt-1 flex items-center gap-1" style="color: rgb(148,163,184);">${icon("clock", "w-3 h-3 inline")} ${d.due_date}</div>` : ""}
          ${d.settled ? `<div class="text-xs mt-1 font-medium" style="color: rgb(16,185,129);">&#10003; ${t("debts.settled")}</div>` : ""}

          ${d.loan && !d.settled ? `
            <button data-action="click->pwa-debts#showLoanDetail" data-id="${d.id}"
                    class="w-full mt-2 py-2 rounded-xl text-xs font-semibold active:scale-[0.98] transition-transform"
                    style="background-color: rgb(241,245,249); color: rgb(99,102,241);">
              ${icon("chart-bar", "w-4 h-4 inline")} ${t("debts.schedule")}
            </button>` : ""}

          ${isPaying ? this.payForm(d) : ""}

          <div class="flex justify-end mt-2">
            <button data-action="click->pwa-debts#delete" data-id="${d.id}"
                    class="text-xs px-2 py-1 rounded-lg active:scale-[0.98] transition-transform"
                    style="color: rgb(148,163,184);">${t("debts.delete")}</button>
          </div>
        </div>`
    }).join("")

    this.listTarget.innerHTML = `<div class="fade-in">${html}</div>`
  }

  loanSummaryCard(d) {
    const locale = window.APP_LOCALE === "ru" ? "ru-RU" : "en-US"
    const payoffLabel = d.projected_payoff
      ? new Date(d.projected_payoff).toLocaleDateString(locale, { month: "short", year: "numeric" })
      : "—"

    return `
      <div style="margin-top: 8px; padding: 10px; border-radius: 10px; background-color: rgb(241,245,249);">
        <div class="flex justify-between text-xs mb-1">
          <span style="color: rgb(148,163,184);">${t("debts.monthly_payment")}</span>
          <span class="font-semibold">${formatMoney(d.monthly_payment, d.currency)}</span>
        </div>
        <div class="flex justify-between text-xs mb-1">
          <span style="color: rgb(148,163,184);">${t("debts.interest_rate")}</span>
          <span class="font-semibold">${d.interest_rate}%</span>
        </div>
        <div class="flex justify-between text-xs mb-1">
          <span style="color: rgb(148,163,184);">${t("debts.months_left")}</span>
          <span class="font-semibold">${d.months_remaining}</span>
        </div>
        <div class="flex justify-between text-xs">
          <span style="color: rgb(148,163,184);">${t("debts.payoff_date")}</span>
          <span class="font-semibold">${payoffLabel}</span>
        </div>
      </div>`
  }

  // ── Loan detail with chart ──

  async showLoanDetail(event) {
    const id = parseInt(event.currentTarget.dataset.id)
    const debt = this.debts.find(d => d.id === id)
    if (!debt) return

    try {
      const data = await apiGet(`/api/debts/${id}/schedule`)
      this.renderLoanDetail(debt, data)
    } catch (e) {
      console.error("Schedule load error:", e)
    }
  }

  renderLoanDetail(debt, data) {
    if (!this.hasLoanDetailTarget) return

    this.listTarget.classList.add("hidden")
    this.summaryTarget.classList.add("hidden")
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.add("hidden")
    this.loanDetailTarget.classList.remove("hidden")

    const schedule = data.schedule || []
    const locale = window.APP_LOCALE === "ru" ? "ru-RU" : "en-US"
    const totalInterest = data.total_interest_remaining
    const totalCost = debt.remaining + totalInterest
    const paidPct = debt.amount > 0 ? Math.min(((debt.amount - debt.remaining) / debt.amount) * 100, 100) : 0
    const firstPmt = schedule[0] || {}
    const principalPct = firstPmt.payment > 0 ? ((firstPmt.principal / firstPmt.payment) * 100).toFixed(0) : 0

    this.loanDetailTarget.innerHTML = `
      <div class="fade-in">
        <!-- Back + title -->
        <div class="flex items-center gap-3 mb-4">
          <button data-action="click->pwa-debts#closeLoanDetail"
                  class="active:scale-[0.98] transition-transform" style="color: rgb(99,102,241); font-size: 20px;">&#8592;</button>
          <div>
            <div class="font-semibold text-sm">${debt.person_name}</div>
            <div class="text-xs" style="color: rgb(148,163,184);">${debt.interest_rate}% · ${debt.months_remaining} ${t("debts.months_left").toLowerCase()} · ${debt.projected_payoff ? new Date(debt.projected_payoff).toLocaleDateString(locale, { month: "long", year: "numeric" }) : ""}</div>
          </div>
        </div>

        <!-- Progress ring + key numbers -->
        <div class="p-4 rounded-2xl mb-4" style="background-color: rgb(248,250,252);">
          <div class="flex items-center gap-4">
            <div style="position: relative; width: 80px; height: 80px; flex-shrink: 0;">
              <svg viewBox="0 0 36 36" style="width: 80px; height: 80px; transform: rotate(-90deg);">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgb(241,245,249)" stroke-width="3"/>
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgb(16,185,129)" stroke-width="3"
                        stroke-dasharray="${paidPct} ${100 - paidPct}" stroke-linecap="round"/>
              </svg>
              <div style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;">
                <span class="text-xs font-bold">${paidPct.toFixed(0)}%</span>
              </div>
            </div>
            <div class="flex-1">
              <div class="text-xs" style="color: rgb(148,163,184);">${t("debts.remaining_balance")}</div>
              <div class="text-lg font-bold" style="color: rgb(239,68,68);">${formatMoney(debt.remaining, debt.currency)}</div>
              <div class="text-xs mt-1" style="color: rgb(148,163,184);">${t("debts.monthly_payment")}: <span class="font-semibold" style="color: inherit;">${formatMoney(data.monthly_payment, debt.currency)}</span></div>
            </div>
          </div>
        </div>

        <!-- Cost breakdown -->
        <div class="p-4 rounded-2xl mb-4" style="background-color: rgb(248,250,252);">
          <div class="text-xs font-semibold mb-3" style="color: rgb(148,163,184);">${t("debts.loan_summary")}</div>
          <div class="flex justify-between mb-2">
            <span class="text-xs" style="color: rgb(148,163,184);">${t("debts.principal")}</span>
            <span class="text-xs font-semibold">${formatMoney(debt.remaining, debt.currency)}</span>
          </div>
          <div class="flex justify-between mb-2">
            <span class="text-xs" style="color: rgb(148,163,184);">${t("debts.total_interest")}</span>
            <span class="text-xs font-semibold" style="color: rgb(99,102,241);">${formatMoney(totalInterest, debt.currency)}</span>
          </div>
          <div style="height: 1px; background: rgba(148,163,184,0.12); margin: 8px 0;"></div>
          <div class="flex justify-between">
            <span class="text-xs font-semibold">${t("debts.total_cost")}</span>
            <span class="text-xs font-bold">${formatMoney(totalCost, debt.currency)}</span>
          </div>
        </div>

        <!-- Stacked bar chart: principal vs interest over time -->
        <div class="p-4 rounded-2xl mb-4" style="background-color: rgb(248,250,252);">
          <div class="flex items-center justify-between mb-2">
            <div class="text-xs font-semibold" style="color: rgb(148,163,184);">${t("debts.payment_structure")}</div>
            <div class="flex items-center gap-3 text-xs" style="color: rgb(148,163,184);">
              <span class="flex items-center gap-1"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:rgb(16,185,129);"></span>${t("debts.principal")}</span>
              <span class="flex items-center gap-1"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:rgb(99,102,241);"></span>${t("debts.interest")}</span>
            </div>
          </div>
          <div style="position: relative; height: 180px; width: 100%;">
            <canvas id="loanChart"></canvas>
          </div>
        </div>

        <!-- Current payment split -->
        <div class="p-3 rounded-2xl mb-4" style="background-color: rgb(248,250,252);">
          <div class="text-xs font-semibold mb-2" style="color: rgb(148,163,184);">${t("debts.next_payment")}: ${formatMoney(data.monthly_payment, debt.currency)}</div>
          <div style="display: flex; height: 8px; border-radius: 4px; overflow: hidden; margin-bottom: 8px;">
            <div style="width: ${principalPct}%; background-color: rgb(16,185,129);"></div>
            <div style="width: ${100 - principalPct}%; background-color: rgb(99,102,241);"></div>
          </div>
          <div class="flex justify-between text-xs">
            <span style="color: rgb(16,185,129);">${t("debts.principal")}: ${formatMoney(firstPmt.principal || 0, debt.currency)} (${principalPct}%)</span>
            <span style="color: rgb(99,102,241);">${t("debts.interest")}: ${formatMoney(firstPmt.interest || 0, debt.currency)}</span>
          </div>
        </div>

        <!-- Schedule list (first 12) -->
        <div class="text-xs font-semibold mb-2 px-1" style="color: rgb(148,163,184);">${t("debts.schedule")}</div>
        <div class="p-3 rounded-2xl" style="background-color: rgb(248,250,252);">
          ${this.scheduleList(schedule.slice(0, 12), debt.currency, locale)}
          ${schedule.length > 12 ? `<div class="text-xs text-center py-2" style="color: rgb(148,163,184);">... +${schedule.length - 12} ${t("debts.months_left").toLowerCase()}</div>` : ""}
        </div>
      </div>`

    requestAnimationFrame(() => this.renderLoanChart(schedule, debt.currency))
  }

  scheduleList(items, currency, locale) {
    return items.map((s, i) => {
      const [year, month] = s.date.split("-")
      const date = new Date(year, month - 1)
      const label = date.toLocaleDateString(locale, { month: "short", year: "2-digit" })

      return `
        <div class="flex items-center justify-between py-2 ${i < items.length - 1 ? "border-b" : ""}"
             style="border-color: rgba(148,163,184,0.08);">
          <div class="text-xs font-medium" style="min-width: 55px;">${label}</div>
          <div class="flex gap-3 text-xs">
            <span style="color: rgb(16,185,129);">${formatMoney(s.principal, currency)}</span>
            <span style="color: rgb(99,102,241);">${formatMoney(s.interest, currency)}</span>
          </div>
          <div class="text-xs" style="color: rgb(148,163,184);">${formatMoney(s.balance, currency)}</div>
        </div>`
    }).join("")
  }

  async renderLoanChart(schedule, currency) {
    const canvas = document.getElementById("loanChart")
    if (!canvas || !schedule.length) return

    const Chart = window.Chart
    if (!Chart) return

    const ctx = canvas.getContext("2d")
    const hintColor = "rgb(148,163,184)"
    const incomeColor = "rgb(16,185,129)"
    const accentColor = "rgb(99,102,241)"

    // Sample ~24 bars for readability
    const step = Math.max(1, Math.floor(schedule.length / 24))
    const sampled = schedule.filter((_, i) => i % step === 0 || i === schedule.length - 1)

    const locale = window.APP_LOCALE === "ru" ? "ru-RU" : "en-US"
    const labels = sampled.map(s => {
      const [y, m] = s.date.split("-")
      return new Date(y, m - 1).toLocaleDateString(locale, { year: "2-digit" })
    })
    // Deduplicate year labels — show only first occurrence
    const seen = new Set()
    const cleanLabels = labels.map(l => { if (seen.has(l)) return ""; seen.add(l); return l })

    if (this.loanChartInstance) this.loanChartInstance.destroy()

    this.loanChartInstance = new Chart(ctx, {
      type: "bar",
      data: {
        labels: cleanLabels,
        datasets: [
          {
            label: t("debts.principal"),
            data: sampled.map(s => s.principal),
            backgroundColor: incomeColor,
            borderRadius: { topLeft: 2, topRight: 2 },
            borderSkipped: "bottom"
          },
          {
            label: t("debts.interest"),
            data: sampled.map(s => s.interest),
            backgroundColor: accentColor + "CC",
            borderRadius: { topLeft: 2, topRight: 2 },
            borderSkipped: "bottom"
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: "easeOutCubic" },
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { color: hintColor, font: { size: 9 }, maxRotation: 0 },
            border: { display: false }
          },
          y: {
            stacked: true,
            grid: { color: "rgba(148,163,184,0.08)" },
            ticks: {
              color: hintColor,
              font: { size: 10 },
              maxTicksLimit: 5,
              callback: (v) => this.compactNumber(v)
            },
            border: { display: false },
            beginAtZero: true
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.85)",
            cornerRadius: 12,
            padding: 12,
            titleFont: { size: 12, weight: "600" },
            bodyFont: { size: 11 },
            bodySpacing: 4,
            callbacks: {
              title: (items) => {
                const idx = items[0].dataIndex
                const s = sampled[idx]
                const [y, m] = s.date.split("-")
                return new Date(y, m - 1).toLocaleDateString(locale, { month: "short", year: "numeric" })
              },
              label: (ctx) => `${ctx.dataset.label}: ${formatMoney(ctx.parsed.y, currency)}`
            }
          }
        }
      }
    })
  }

  closeLoanDetail() {
    if (this.hasLoanDetailTarget) this.loanDetailTarget.classList.add("hidden")
    this.listTarget.classList.remove("hidden")
    if (this.debts.length) this.summaryTarget.classList.remove("hidden")
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.remove("hidden")
    if (this.loanChartInstance) {
      this.loanChartInstance.destroy()
      this.loanChartInstance = null
    }
  }

  // ── Loan form toggle ──

  toggleLoan() {
    this.isLoan = this.loanToggleTarget.checked
    if (this.hasLoanFieldsTarget) {
      this.loanFieldsTarget.classList.toggle("hidden", !this.isLoan)
    }
    // Toggle switch visual
    if (this.hasLoanSliderTarget) {
      this.loanSliderTarget.style.opacity = this.isLoan ? "1" : "0.3"
      this.loanSliderTarget.style.backgroundColor = this.isLoan ? "rgb(99,102,241)" : "rgb(148,163,184)"
    }
    if (this.hasLoanKnobTarget) {
      this.loanKnobTarget.style.transform = this.isLoan ? "translateX(20px)" : "translateX(0)"
    }
    // Loans are always i_owe
    if (this.isLoan && this.direction !== "i_owe") {
      this.direction = "i_owe"
      this.updateDirectionBtns()
    }
  }

  // ── Payment form ──

  payForm(d) {
    const isIOwe = d.direction === "i_owe"
    const accountLabel = isIOwe ? t("debts.pay_from") : t("debts.receive_to")
    const btnLabel = isIOwe ? t("debts.pay") : t("debts.receive")
    const btnColor = isIOwe ? "rgb(239,68,68)" : "rgb(16,185,129)"
    const matchingAccounts = this.accounts.filter(a => a.currency === d.currency)
    const accountOptions = matchingAccounts.map(a =>
      `<option value="${a.id}">${a.icon} ${a.name} (${a.currency})</option>`
    ).join("")
    const noAccounts = matchingAccounts.length === 0

    // For loans, default to monthly_payment amount
    const defaultAmount = d.loan ? d.monthly_payment : d.remaining
    const maxAmount = d.loan ? undefined : d.remaining

    return `
      <div class="fade-in" style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(148,163,184,0.1);">
        ${d.loan ? `<div class="text-xs mb-2" style="color: rgb(148,163,184);">${t("debts.monthly_payment")}: ${formatMoney(d.monthly_payment, d.currency)}</div>` : ""}
        ${noAccounts ? `<div class="text-xs mb-2" style="color: rgb(239,68,68);">${t("debts.no_matching_account")} ${d.currency}</div>` : ""}
        <div class="flex gap-2 mb-2">
          <input type="number" data-pay-amount="${d.id}" value="${defaultAmount}" step="0.01" min="0.01"
                 ${maxAmount ? `max="${maxAmount}"` : ""}
                 placeholder="${t("debts.amount_placeholder")}"
                 class="flex-1"
                 style="padding: 8px 10px; border-radius: 10px; font-size: 13px;
                        background-color: rgb(241,245,249); color: inherit;
                        border: 1px solid rgba(148,163,184,0.2); outline: none;" />
          <div style="font-size: 12px; display: flex; align-items: center; color: rgb(148,163,184);">${d.currency}</div>
        </div>
        <div class="mb-2">
          <div class="text-xs mb-1" style="color: rgb(148,163,184);">${accountLabel}</div>
          <select data-pay-account="${d.id}" ${noAccounts ? "disabled" : ""}
                  style="width: 100%; padding: 8px 10px; border-radius: 10px; font-size: 13px;
                         background-color: rgb(241,245,249); color: inherit;
                         border: 1px solid rgba(148,163,184,0.2);
                         -webkit-appearance: none; appearance: none;">
            ${accountOptions}
          </select>
        </div>
        <div class="flex gap-2">
          <button data-action="click->pwa-debts#cancelPay"
                  class="flex-1 text-xs py-2 rounded-xl font-semibold active:scale-[0.98] transition-transform"
                  style="background-color: rgb(241,245,249); color: rgb(148,163,184);">
            ${t("debts.cancel")}
          </button>
          <button data-action="click->pwa-debts#submitPay" data-id="${d.id}" ${noAccounts ? "disabled" : ""}
                  class="flex-1 text-xs py-2 rounded-xl font-semibold active:scale-[0.98] transition-transform"
                  style="background-color: ${noAccounts ? "rgb(241,245,249)" : btnColor}; color: ${noAccounts ? "rgb(148,163,184)" : "white"};">
            ${btnLabel}
          </button>
        </div>
      </div>`
  }

  togglePay(event) {
    const id = parseInt(event.currentTarget.dataset.id)
    this.payingDebtId = this.payingDebtId === id ? null : id
    this.renderList()
  }

  cancelPay() {
    this.payingDebtId = null
    this.renderList()
  }

  async submitPay(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.id
    const amountInput = this.element.querySelector(`[data-pay-amount="${id}"]`)
    const accountSelect = this.element.querySelector(`[data-pay-account="${id}"]`)
    const amount = parseFloat(amountInput?.value)
    const accountId = accountSelect?.value

    if (!amount || amount <= 0 || !accountId) {
      if (amountInput) amountInput.style.borderColor = "rgb(239,68,68)"
      return
    }

    try {
      const result = await apiPut(`/api/debts/${id}/pay`, { amount, account_id: accountId })
      this.payingDebtId = null

      // For loans: ask how to recalculate after early repayment
      if (result.loan && !result.settled) {
        this.showRecalculateChoice(id, result)
      } else {
        this.load()
      }
    } catch (e) {
      console.error("Pay debt error:", e)
    }
  }

  showRecalculateChoice(debtId, debt) {
    const overlay = document.createElement("div")
    overlay.id = "recalculate-overlay"
    overlay.style.cssText = "position:fixed;inset:0;z-index:1000;display:flex;align-items:flex-end;justify-content:center;background:rgba(0,0,0,0.5);backdrop-filter:blur(4px);"
    overlay.innerHTML = `
      <div style="width:100%;max-width:420px;padding:20px 16px env(safe-area-inset-bottom, 16px);border-radius:20px 20px 0 0;background:white;" class="fade-in">
        <div class="text-sm font-semibold mb-1">${t("debts.recalculate_title")}</div>
        <div class="text-xs mb-4" style="color:rgb(148,163,184);">${t("debts.recalculate_hint")}</div>

        <button data-strategy="reduce_payment" data-debt-id="${debtId}"
                data-action="click->pwa-debts#recalculate"
                class="w-full py-3 rounded-xl text-sm font-semibold mb-2 active:scale-[0.98] transition-transform"
                style="background:rgb(99,102,241);color:white;">
          ${t("debts.reduce_payment")}
        </button>
        <button data-strategy="reduce_term" data-debt-id="${debtId}"
                data-action="click->pwa-debts#recalculate"
                class="w-full py-3 rounded-xl text-sm font-semibold mb-2 active:scale-[0.98] transition-transform"
                style="background:rgb(248,250,252);color:inherit;">
          ${t("debts.reduce_term")}
        </button>
        <button data-action="click->pwa-debts#dismissRecalculate"
                class="w-full py-3 rounded-xl text-sm font-semibold active:scale-[0.98] transition-transform"
                style="background:transparent;color:rgb(148,163,184);">
          ${t("debts.keep_current")}
        </button>
      </div>`
    this.element.appendChild(overlay)
  }

  async recalculate(event) {
    const strategy = event.currentTarget.dataset.strategy
    const debtId = event.currentTarget.dataset.debtId
    try {
      await apiPut(`/api/debts/${debtId}/recalculate`, { strategy })
    } catch (e) {
      console.error("Recalculate error:", e)
    }
    this.dismissRecalculate()
  }

  dismissRecalculate() {
    const overlay = document.getElementById("recalculate-overlay")
    if (overlay) overlay.remove()
    this.load()
  }

  // ── Form ──

  showForm() {
    if (this.hasFormTarget) this.formTarget.classList.remove("hidden")
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.add("hidden")
  }

  cancelForm() {
    if (this.hasFormTarget) this.formTarget.classList.add("hidden")
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.remove("hidden")
    this.resetForm()
  }

  setDirection(event) {
    this.direction = event.currentTarget.dataset.direction
    this.updateDirectionBtns()
  }

  updateDirectionBtns() {
    if (this.hasDirIOweBtnTarget && this.hasDirOwedBtnTarget) {
      if (this.direction === "i_owe") {
        this.dirIOweBtnTarget.style.backgroundColor = "rgb(239,68,68)"
        this.dirIOweBtnTarget.style.color = "white"
        this.dirOwedBtnTarget.style.backgroundColor = "rgb(248,250,252)"
        this.dirOwedBtnTarget.style.color = "rgb(148,163,184)"
      } else {
        this.dirOwedBtnTarget.style.backgroundColor = "rgb(16,185,129)"
        this.dirOwedBtnTarget.style.color = "white"
        this.dirIOweBtnTarget.style.backgroundColor = "rgb(248,250,252)"
        this.dirIOweBtnTarget.style.color = "rgb(148,163,184)"
      }
    }
  }

  async save() {
    if (!networkStatus.online) { alertOffline(); return }
    const personName = this.personNameTarget?.value?.trim()
    const amount = parseFloat(this.amountTarget?.value)
    if (!personName || !amount || amount <= 0) return

    const payload = {
      person_name: personName,
      amount,
      currency: this.hasCurrencyTarget ? this.currencyTarget.value : null,
      direction: this.direction,
      note: this.noteTarget?.value?.trim() || null,
      due_date: this.dueDateTarget?.value || null
    }

    if (this.isLoan) {
      const rate = parseFloat(this.interestRateTarget?.value)
      const term = parseInt(this.termMonthsTarget?.value)
      if (!rate || rate <= 0 || !term || term <= 0) {
        return
      }
      payload.interest_rate = rate
      payload.term_months = term
      const knownPmt = parseFloat(this.knownPaymentTarget?.value)
      if (knownPmt > 0) payload.monthly_payment = knownPmt
    }

    try {
      const result = await apiPost("/api/debts", { debt: payload })
      this.cancelForm()

      if (result.warning === "payment_below_interest") {
        this.showPaymentWarning(result.id, result.min_payment, result.currency)
      } else {
        this.load()
      }
    } catch (e) {
      if (e.status === 403) window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "debts" } }))
      else console.error("Save debt error:", e)
    }
  }

  async delete(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.id
    if (!confirm(t("debts.delete_confirm"))) return

    try {
      await apiDelete(`/api/debts/${id}`)
      this.load()
    } catch (e) {
      console.error("Delete debt error:", e)
    }
  }

  resetForm() {
    if (this.hasPersonNameTarget) this.personNameTarget.value = ""
    if (this.hasAmountTarget) this.amountTarget.value = ""
    if (this.hasNoteTarget) this.noteTarget.value = ""
    if (this.hasDueDateTarget) this.dueDateTarget.value = ""
    if (this.hasInterestRateTarget) this.interestRateTarget.value = ""
    if (this.hasTermMonthsTarget) this.termMonthsTarget.value = ""
    this.direction = "i_owe"
    this.isLoan = false
    if (this.hasLoanToggleTarget) this.loanToggleTarget.checked = false
    if (this.hasLoanFieldsTarget) this.loanFieldsTarget.classList.add("hidden")
    if (this.hasLoanSliderTarget) {
      this.loanSliderTarget.style.opacity = "0.3"
      this.loanSliderTarget.style.backgroundColor = "rgb(148,163,184)"
    }
    if (this.hasLoanKnobTarget) this.loanKnobTarget.style.transform = "translateX(0)"
    this.updateDirectionBtns()
  }

  showPaymentWarning(debtId, minPayment, currency) {
    const overlay = document.createElement("div")
    overlay.id = "payment-warning-overlay"
    overlay.style.cssText = "position:fixed;inset:0;z-index:1000;display:flex;align-items:flex-end;justify-content:center;background:rgba(0,0,0,0.5);backdrop-filter:blur(4px);"
    overlay.innerHTML = `
      <div style="width:100%;max-width:420px;padding:20px 16px env(safe-area-inset-bottom, 16px);border-radius:20px 20px 0 0;background:white;" class="fade-in">
        <div class="text-sm font-semibold mb-1">${t("debts.payment_warning_title")}</div>
        <div class="text-xs mb-4" style="color:rgb(148,163,184);">${t("debts.payment_warning_hint")}</div>

        <button data-action="click->pwa-debts#useMinPayment" data-debt-id="${debtId}" data-min-payment="${minPayment}"
                class="w-full py-3 rounded-xl text-sm font-semibold mb-2 active:scale-[0.98] transition-transform"
                style="background:rgb(99,102,241);color:white;">
          ${t("debts.use_min_payment")} (${formatMoney(minPayment, currency)})
        </button>
        <button data-action="click->pwa-debts#dismissPaymentWarning"
                class="w-full py-3 rounded-xl text-sm font-semibold active:scale-[0.98] transition-transform"
                style="background:transparent;color:rgb(148,163,184);">
          ${t("debts.keep_current")}
        </button>
      </div>`
    this.element.appendChild(overlay)
  }

  async useMinPayment(event) {
    const debtId = event.currentTarget.dataset.debtId
    const minPayment = parseFloat(event.currentTarget.dataset.minPayment)
    try {
      await apiPut(`/api/debts/${debtId}`, { debt: { monthly_payment: minPayment } })
    } catch (e) {
      console.error("Update min payment error:", e)
    }
    this.dismissPaymentWarning()
  }

  dismissPaymentWarning() {
    const overlay = document.getElementById("payment-warning-overlay")
    if (overlay) overlay.remove()
    this.load()
  }

  compactNumber(value) {
    if (value >= 1000000) return (value / 1000000).toFixed(1) + "M"
    if (value >= 1000) return (value / 1000).toFixed(0) + "K"
    return value.toString()
  }
}
